'use client';
import { useCallback } from 'react';
import {
  resolveActiveSortOption,
  SORT_QUERY_PARAM,
} from './resolveActiveSortOption';
import { ProductSortConfig, ProductSortOption } from './types';
import { usePanelQueryParams } from './usePanelQueryParams';

export type UseProductSortResult = {
  /** The option currently in effect. Undefined when nothing is configured. */
  activeOption: ProductSortOption | undefined;
  /** Swaps the current history entry; a sort change never adds a Back step. */
  setSortValue: (value: string) => void;
};

/**
 * The active option is derived from the URL rather than held in state, so the
 * table and the cards cannot drift and a deep link takes the same path as a
 * click. `useSafeSearchParams`, not `useSearchParams`, keeps the page cacheable.
 */
export const useProductSort = (
  config: ProductSortConfig | undefined,
): UseProductSortResult => {
  const paramName = SORT_QUERY_PARAM;
  const { readParam, paramsKey, write } = usePanelQueryParams([paramName]);

  // Deferred to the render after hydration: the server cannot read the param
  // without dropping the static cache, so applying it earlier would not match
  // the served HTML. The observer populating the context may not have fired yet.
  const paramValue =
    paramsKey === null ? undefined : (readParam(paramName) ?? undefined);

  const activeOption = resolveActiveSortOption(config, paramValue);

  // Replace rather than push, the same as a filter change (CMS-1903 R10.6):
  // three sort changes cost three Back presses otherwise, and Back should
  // leave the page. Raised by QA on CMS-1871, comment 219633.
  const setSortValue = useCallback(
    (value: string) => write((params) => params.set(paramName, value)),
    [paramName, write],
  );

  return { activeOption, setSortValue };
};
